import React, { FC } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer, Text } from 'recharts';
import { AnalyticsChartColorsArray } from '../../enums/analytics-chart-colors.enum';

export type EngagementLineChartComponentProps = {
	data?: any;
};

export const EngagementLineChartComponent: FC<EngagementLineChartComponentProps> = (props) => {
	const { data } = props;
	const appTheme = useTheme();
	const uniqueYears = new Set(data.map((item: any) => new Date(item._id.monthStart).getFullYear()));
	const showYear = uniqueYears.size > 1;
	const grouped: any = {};

	const allKeys: Set<any> = new Set();
	data.forEach((item: any) => {
		const key = item._id.lineDataKey ?? 'Not provided';
		allKeys.add(key);
	});

	data.forEach((item: any) => {
		const { _id, count } = item;
		const key = _id.lineDataKey ?? 'Not provided';
		const date = new Date(_id.monthStart);
		const label = date.toLocaleString('eng', {
			month: 'short',
			...(showYear && { year: 'numeric' }),
		});

		if (!grouped[label]) grouped[label] = { month: label };

		grouped[label][key] = count;
	});

	const allLineKeys = Array.from(allKeys);
	const finalChartData = Object.values(grouped).map((entry: any) => {
		allLineKeys.forEach((key: string) => {
			if (!(key in entry)) {
				entry[key] = 0;
			}
		});
		return entry;
	});

	const lineKeys = Object.keys(finalChartData[0] || {}).filter((key) => key !== 'month');

	const totalsByKey: any = {};

	finalChartData.forEach((row: any) => {
		for (const [key, value] of Object.entries(row)) {
			if (key !== 'month') {
				totalsByKey[key] = (totalsByKey[key] || 0) + value;
			}
		}
	});

	const top3LineCounts = Object.entries(totalsByKey)
		.sort((a: any, b: any) => b[1] - a[1])
		.slice(0, 3)
		.map((item: any) => item[1]);

	const top3LineKeys = Object.entries(totalsByKey)
		.sort((a: any, b: any) => b[1] - a[1])
		.slice(0, 3)
		.map(([key]) => key);

	const CustomLegend = (props: any) => (
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', mb: 2 }}>
			{props.payload
				.filter((entry: any) => top3LineKeys.includes(entry.value))
				.map((entry: any) => (
					<Box key={entry.value} style={{ color: entry.color }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
							<Box
								sx={{
									height: 10,
									width: 10,
									borderRadius: '50%',
									backgroundColor: entry.color,
									flexShrink: 0,
								}}
							></Box>
							<Typography data-overflow="true" variant="body2" sx={{ color: '#797F92', fontSize: '14px' }}>
								{entry.value}
							</Typography>
						</Box>
					</Box>
				))}
		</Box>
	);

	const CustomTooltip = (props: any) => {
		const { active, payload, label } = props;

		if (!active || !payload || !payload.length) return null;

		return (
			<Box
				sx={{
					width: '170px',
					maxHeight: '170px',
					overflowY: 'auto',
					backgroundColor: 'white',
					border: '1px solid #ccc',
					borderRadius: '6px',
					padding: '10px',
					boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
					pointerEvents: 'auto',
					py: 2,
					px: 2,
				}}
			>
				<Typography sx={{ mb: '10px', fontWeight: 'bold', mt: 0 }}>{label}</Typography>
				{payload.map((entry: any, index: number) => (
					<Box
						key={index}
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							gap: '8px',
							marginBottom: '6px',
						}}
					>
						<Box
							component="span"
							sx={{
								flex: 1,
								color: entry.color,
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							}}
							title={entry.name}
						>
							{entry.name}
						</Box>
						<Box component="span" sx={{ whiteSpace: 'nowrap' }}>
							{entry.value}
						</Box>
					</Box>
				))}
			</Box>
		);
	};

	return (
		<ResponsiveContainer width="100%" height="100%">
			<LineChart data={finalChartData?.length > 0 ? finalChartData : [{ x: '', y: 0 }]}>
				<CartesianGrid strokeDasharray="3 3" syncWithTicks={true} />
				<XAxis
					dataKey="month"
					padding={{ left: 20, right: 20 }}
					tick={({ x, y, payload, textAnchor, ...rest }) => (
						<text x={x} y={y + 10} orientation={'bottom'} textAnchor={'middle'} {...rest}>
							<tspan fontSize={12}>{payload.value}</tspan>
						</text>
					)}
				/>
				<YAxis
					allowDecimals={false}
					scale={top3LineCounts[0] > 10 ? 'sqrt' : 'auto'}
					domain={[0, 'auto']}
					padding={{ top: 20 }}
					tick={({ x, y, payload, textAnchor, ...rest }) => (
						<text x={x} y={y + 5} textAnchor={'end'} {...rest} fill={appTheme.colors.grayLightBlue} fontWeight={500}>
							<tspan fontSize={12}>{payload.value}</tspan>
						</text>
					)}
					tickSize={16}
				/>
				<Tooltip content={CustomTooltip} />
				<Legend content={<CustomLegend />} verticalAlign="top" align="right" />
				{lineKeys.map((key: string, index: number) => (
					<Line
						type="linear"
						dataKey={key}
						stroke={AnalyticsChartColorsArray[index % AnalyticsChartColorsArray.length]}
						strokeWidth={2}
						dot={false}
						activeDot={{ r: 5 }}
					/>
				))}
			</LineChart>
		</ResponsiveContainer>
	);
};
